import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Text, Tooltip, Wrapper } from 'components';
import { HTTPStatusCode } from 'config/const';
import { Appearance } from 'design/types';
import { clearRequest, selectAPIRequestErrors } from 'store/ApiClient';
import { selectSite, Site } from 'store/Site';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IEmployerSite, PermissionType } from 'types';
import { hasSitePermission } from 'utils/employers';
import { SettingCardContainer, StyledIconButton } from '../../../styles';
import { TipCalculationsDescription } from '../../../TipCalculator/shared/styles';
import { StyledTag } from '../../shared/styles';
import { BusinessDayStartForm } from './BusinessDayStartForm';
import { BusinessDayStartItem } from './BusinessDayStartItem';

interface IBusinessDayStartProps {
  currentSite: IEmployerSite;
}

const getErrorTag = (error?: IApiErrorPayloadWithCode) => {
  if (!error) return null;

  const message =
    error.statusCode === HTTPStatusCode.CONFLICT
      ? { label: 'This time conflicts with an existing day segment.\nAdjust the start time or update your day segments before saving.', dataCy: 'business-day-start-conflict-error' }
      : { label: 'An error occurred. Please try again.', dataCy: 'business-day-start-error' };

  return <StyledTag appearance={Appearance.DANGER} label={message.label} dataCy={message.dataCy} />;
};

export const BusinessDayStart: React.FC<IBusinessDayStartProps> = ({ currentSite }) => {
  const dispatch = useDispatch();
  const siteWithSettings = useSelector(selectSite());
  const error = useSelector((state: IStore) => selectAPIRequestErrors(state, Site.UPDATE_BUSINESS_DAY_START_REQUEST)) as IApiErrorPayloadWithCode;
  const [isEditing, setIsEditing] = useState(false);
  const onEdit = () => {
    dispatch(clearRequest(Site.UPDATE_BUSINESS_DAY_START_REQUEST));
    setIsEditing(true);
  };
  const onClose = () => setIsEditing(false);
  const editPermission = hasSitePermission(currentSite?.permissions, PermissionType.SITE_DAYSEGMENT_UPDATE);
  const { businessDayStart, timezone } = siteWithSettings?.settings || {};

  return (
    <SettingCardContainer data-cy="business-day-start-settings" disabled={!isEditing}>
      {isEditing && getErrorTag(error)}

      <Wrapper direction="row" justifyContent="space-between" gap={0} noWrap>
        <Wrapper direction="column" gap={4}>
          <Text size="small" fontWeight="semibold">
            Start of Business Day
          </Text>
          <TipCalculationsDescription size="xsmall">Tip calculations follow your business day, not the calendar day. Set this time to match your POS's start-of-day setting. For most sites, 4:00 AM works well.</TipCalculationsDescription>
        </Wrapper>
        {!isEditing && (
          <>
            <StyledIconButton label="Edit" appearance="white" icon="Edit" data-cy="edit-button" onClick={onEdit} disabled={!editPermission} tooltipId="no-business-day-start-permission" />
            {!editPermission && (
              <Tooltip id="no-business-day-start-permission">
                You do not have permission to edit the start of business day.
                <br />
                Contact your supervisor for more information.
              </Tooltip>
            )}
          </>
        )}
      </Wrapper>

      {!isEditing ? <BusinessDayStartItem businessDayStart={businessDayStart} timezone={timezone} /> : <BusinessDayStartForm site={siteWithSettings} onClose={onClose} />}
    </SettingCardContainer>
  );
};
